/**
 * Clinic reviews. Fills `sections/clinic-reviews.liquid` from the public feed
 * the Reputon Google Reviews widget reads (no key, CORS open, cached 60s):
 *
 *   grw.reputon.com/app/storefront/widget?shop=<shop>
 *     -> business[]: address, reviews[] (authorName, rating, text,
 *        relativeTimeDescription, time, hide)
 *
 * "This clinic" keeps the one listing whose postcode matches the page's
 * Clinic: Address; otherwise every listing is used. Reviews at or above the
 * minimum rating with text, newest first, `per page` at a time with Show more.
 *
 * The feed is fetched once per page, when the first section nears the screen.
 * Every value goes in with textContent. If the feed fails or nothing passes,
 * the section hides.
 */
(function () {
  var FEED = 'https://grw.reputon.com/app/storefront/widget';
  var feeds = {};

  function getFeed(shop) {
    if (!feeds[shop]) {
      feeds[shop] = fetch(FEED + '?shop=' + encodeURIComponent(shop) + '&lang=en', {
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('clinic reviews ' + response.status);
        return response.json();
      });
      feeds[shop].catch(function () { delete feeds[shop]; });
    }
    return feeds[shop];
  }

  // A UK postcode, spaces removed and upper-cased: "w1g 7hq" -> "W1G7HQ".
  function postcode(text) {
    var match = /([A-Z]{1,2}[0-9][A-Z0-9]?)\s*([0-9][A-Z]{2})\b/i.exec(text || '');
    return match ? (match[1] + match[2]).toUpperCase() : '';
  }

  function shortName(name, format) {
    var parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (format === 'full' || parts.length < 2) return parts.join(' ');
    return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase();
  }

  function init(section) {
    if (section.dataset.reviewsReady === 'true') return;
    section.dataset.reviewsReady = 'true';

    var shop = section.dataset.shop;
    var wanted = postcode(section.dataset.address);
    var minRating = Number(section.dataset.minRating) || 1;
    var perPage = Number(section.dataset.perPage) || 3;
    var nameFormat = section.dataset.nameFormat;
    var list = section.querySelector('[data-reviews-list]');
    var more = section.querySelector('[data-reviews-more]');
    var template = section.querySelector('[data-reviews-template]');
    var starTemplate = section.querySelector('[data-reviews-star]');
    if (!shop || !list || !template) return;
    if (section.dataset.address && !wanted) {
      section.hidden = true;
      return;
    }

    var reviews = [];
    var shown = 0;

    function starRow(node, rating) {
      if (!starTemplate) return;
      node.setAttribute('aria-label', rating + ' out of 5');
      for (var i = 1; i <= 5; i++) {
        var star = starTemplate.content.firstElementChild.cloneNode(true);
        if (i > rating) star.classList.add('opacity-25');
        star.setAttribute('aria-hidden', 'true');
        node.appendChild(star);
      }
    }

    function render(review) {
      var item = template.content.firstElementChild.cloneNode(true);
      var name = shortName(review.authorName, nameFormat);
      var date = (review.relativeTimeDescription || '').trim();

      starRow(item.querySelector('[data-review-stars]'), Number(review.rating) || 0);
      item.querySelector('[data-review-title]').remove();
      item.querySelector('[data-review-text]').textContent = review.text.trim();
      item.querySelector('[data-review-author]').textContent = name;
      item.querySelector('[data-review-date]').textContent = date;
      if (!name || !date) item.querySelector('[data-review-divider]').remove();
      item.querySelector('[data-review-verified]').hidden = false;
      list.appendChild(item);
    }

    function showMore() {
      reviews.slice(shown, shown + perPage).forEach(render);
      shown = Math.min(shown + perPage, reviews.length);
      if (more) more.hidden = shown >= reviews.length;
    }

    function start() {
      getFeed(shop)
        .then(function (data) {
          var seen = {};
          ((data && data.business) || []).forEach(function (business) {
            if (wanted && postcode(business.address) !== wanted) return;
            (business.reviews || []).forEach(function (review) {
              if (!review || review.hide || seen[review.id]) return;
              if ((Number(review.rating) || 0) < minRating) return;
              if (!(review.text || '').trim()) return;
              seen[review.id] = true;
              reviews.push(review);
            });
          });
          reviews.sort(function (a, b) { return (b.time || 0) - (a.time || 0); });
          if (!reviews.length) {
            section.hidden = true;
            return;
          }
          showMore();
        })
        .catch(function () {
          section.hidden = true;
        });
    }

    if (more) more.addEventListener('click', showMore);

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          observer.disconnect();
          start();
        }
      }, { rootMargin: '600px 0px' });
      observer.observe(section);
    } else {
      start();
    }
  }

  function run(scope) {
    (scope || document).querySelectorAll('[data-clinic-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function (event) {
    run(event.target);
  });
})();
